/**
 * Vendors.jsx — Lead Vendors list page (Ringy-parity, full rebuild)
 *
 * Shows vendor cards with name, color dot, lead count, masked API key, last lead received.
 * Per-card actions: Edit / View Integration / Regenerate Key / Email Vendor / Delete.
 * Includes IntegrationDocsModal for in-app docs viewing.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Lock, Webhook, Mail, FileText, KeyRound, Copy, Check, Activity,
  Settings2, Eye, EyeOff, RefreshCw, Trash2, Send, Code2, ArrowRight,
  BarChart2, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import { fmtFromNow } from '../lib/format.js';

function copyText(t) {
  try { navigator.clipboard.writeText(t); toast.success('Copied'); } catch { toast.error('Copy failed'); }
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function Vendors() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ['lead-vendors'],
    queryFn: () => apiGet('/lead-vendors'),
  });
  const vendors = data?.vendors || [];
  const [integrationVendorId, setIntegrationVendorId] = useState(null);

  async function handleDelete(vendorId) {
    if (!confirm('Archive this vendor? Leads already received will not be affected.')) return;
    try {
      await apiDel(`/lead-vendors/${vendorId}`);
      toast.success('Vendor archived');
      refetch();
    } catch { toast.error('Delete failed'); }
  }

  async function handleRegenKey(vendorId) {
    if (!confirm('Rotate API key? The old key will stop working immediately.')) return;
    try {
      const r = await apiPost(`/lead-vendors/${vendorId}/regenerate-key`);
      if (r?.ok) { toast.success('API key rotated'); refetch(); }
    } catch { toast.error('Key rotation failed'); }
  }

  async function handleSendEmail(vendorId) {
    try {
      await apiPost(`/lead-vendors/${vendorId}/send-email`);
      toast.success('Integration email sent');
      refetch();
    } catch (err) { toast.error(err.message || 'Email failed'); }
  }

  return (
    <div className="space-y-4 max-w-[1500px] mx-auto">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Lead Sources</div>
          <h1 className="font-display text-3xl font-bold mt-1">Lead Vendors</h1>
          <div className="text-sm text-white/55 mt-1">
            Connect lead vendors via API key. Each vendor gets a unique intake URL — just share their credentials.
          </div>
        </div>
        <button
          onClick={() => navigate('/vendors/add')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Lead Vendor
        </button>
      </header>

      {/* Vendor list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vendors.map((v) => (
          <VendorCard
            key={v._id}
            v={v}
            onEdit={() => navigate(`/vendors/edit/${v._id}`)}
            onViewIntegration={() => setIntegrationVendorId(v._id)}
            onRegenKey={() => handleRegenKey(v._id)}
            onSendEmail={() => handleSendEmail(v._id)}
            onDelete={() => handleDelete(v._id)}
          />
        ))}
        {vendors.length === 0 && (
          <div className="col-span-full panel p-12 text-center space-y-3">
            <div className="text-white/40 text-sm">No lead vendors yet.</div>
            <button
              onClick={() => navigate('/vendors/add')}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add your first vendor
            </button>
          </div>
        )}
      </div>

      {/* Integration docs modal */}
      {integrationVendorId && (
        <IntegrationDocsModal
          vendorId={integrationVendorId}
          onClose={() => setIntegrationVendorId(null)}
          onSendEmail={() => handleSendEmail(integrationVendorId)}
        />
      )}
    </div>
  );
}

// ─── Vendor card ──────────────────────────────────────────────────────────────

function VendorCard({ v, onEdit, onViewIntegration, onRegenKey, onSendEmail, onDelete }) {
  const [showKey, setShowKey] = useState(false);
  // Marketing-engine PR-A: per-vendor Lead Type assignment dropdown.
  // Persists via PATCH `/lead-vendors/:id`.
  const qc = useQueryClient();
  const { data: ltData } = useQuery({
    queryKey: ['lead-types-for-vendor-card'],
    queryFn: () => apiGet('/lead-types'),
    staleTime: 30_000,
  });
  const leadTypes = ltData?.leadTypes || [];
  const currentLeadTypeId = v.leadType?._id || v.leadType || '';

  async function setLeadType(id) {
    try {
      await apiPut(`/lead-vendors/${v._id}`, { leadType: id || null });
      toast.success('Lead Type updated');
      qc.invalidateQueries({ queryKey: ['lead-vendors'] });
    } catch (e) { toast.error(e.message); }
  }

  const distLabel = {
    account: 'Account import',
    pool: 'Lead pool',
    powerDialer: 'Power Dialer',
    liveTransfer: 'Live Transfer',
    distributeAgents: 'Distributed',
  }[v.distribution?.mode] || 'Account import';

  return (
    <div className="panel p-5 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: v.color || '#8b5cf6' }}
          />
          <div>
            <div className="font-display font-bold text-base leading-tight">{v.name}</div>
            <div className="text-xs text-white/45 mt-0.5">{v.vendorEmail || v.contactEmail || '—'}</div>
          </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-civix-purple/15 text-civix-purpleSoft border border-civix-purple/20">
          {distLabel}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-[10px] text-white/40 uppercase">Total leads</div>
          <div className="gradient-text font-bold text-sm">{(v.leadCount || v.stats?.totalDelivered || 0).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] text-white/40 uppercase">Cost / lead</div>
          <div className="gradient-text font-bold text-sm">
            ${(v.defaultCostPerLead || v.pricePerLead || 0).toFixed(2)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-white/40 uppercase">Last lead</div>
          <div className="text-white/60 text-xs mt-0.5">
            {v.stats?.lastLeadAt ? fmtFromNow(v.stats.lastLeadAt) : '—'}
          </div>
        </div>
      </div>

      {/* Lead Type assignment (Marketing engine PR-A) */}
      <div className="text-xs">
        <label className="block text-[10px] text-white/40 uppercase mb-1">Lead Type</label>
        <select
          aria-label="Lead Type"
          className="input w-full text-xs"
          value={currentLeadTypeId}
          onChange={(e) => setLeadType(e.target.value)}
        >
          <option value="">— Unassigned —</option>
          {leadTypes.map((lt) => (
            <option key={lt._id} value={lt._id}>{lt.name}</option>
          ))}
        </select>
        {!currentLeadTypeId && (
          <div className="text-[10px] text-white/40 mt-1">
            New leads from this vendor will not be auto-tagged. Pick a type to enable Marketing rules.
          </div>
        )}
      </div>

      {/* API key display */}
      <div className="p-2 rounded-lg bg-civix-bg/50 border border-civix-border/40">
        <div className="text-[10px] text-white/40 uppercase mb-1 flex items-center justify-between">
          <span>API Key</span>
          <button
            onClick={() => setShowKey(!showKey)}
            className="text-white/40 hover:text-white/70 transition"
            title={showKey ? 'Hide key' : 'Reveal key'}
          >
            {showKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
        </div>
        <div className="font-mono text-[10px] text-civix-cyan break-all">
          {showKey ? (v.apiKeyFull || v.apiKeyMasked || '—') : (v.apiKeyMasked || '••••••••' + '●'.repeat(32) + '••••••••')}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-1.5 mt-auto">
        <button
          onClick={onEdit}
          className="btn-ghost text-xs flex items-center justify-center gap-1"
        >
          <Settings2 className="w-3.5 h-3.5" />
          Edit
        </button>
        <button
          onClick={onViewIntegration}
          className="btn-ghost text-xs flex items-center justify-center gap-1"
        >
          <Code2 className="w-3.5 h-3.5" />
          Integration
        </button>
        <button
          onClick={onRegenKey}
          className="btn-ghost text-xs flex items-center justify-center gap-1 text-amber-400/80 hover:text-amber-400"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Regen Key
        </button>
        <button
          onClick={onSendEmail}
          className="btn-ghost text-xs flex items-center justify-center gap-1 text-sky-400/80 hover:text-sky-400"
        >
          <Send className="w-3.5 h-3.5" />
          Email Vendor
        </button>
        <button
          onClick={onDelete}
          className="btn-ghost text-xs flex items-center justify-center gap-1 text-red-400/70 hover:text-red-400 col-span-2"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Integration Docs Modal ───────────────────────────────────────────────────

function IntegrationDocsModal({ vendorId, onClose, onSendEmail }) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['integration-docs', vendorId],
    queryFn: () => apiGet(`/lead-vendors/${vendorId}/integration-docs`),
    enabled: !!vendorId,
  });
  const docs = data?.docs;

  function copy(text, label) {
    try {
      navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
      toast.success('Copied!');
    } catch { toast.error('Copy failed'); }
  }

  function copyAllDocs() {
    if (!docs) return;
    const text = [
      `CIVIX Lead Vendor Integration Docs`,
      ``,
      `Endpoint: POST ${docs.endpoint}`,
      ``,
      `Headers:`,
      `  Content-Type: application/json`,
      ``,
      `API Key: ${docs.apiKey}`,
      ``,
      `Sample Payload:`,
      JSON.stringify(docs.samplePayload, null, 2),
      ``,
      `Field Reference:`,
      docs.fieldReference?.map((f) => `  ${f.name} (${f.type}) — ${f.required ? 'Required' : 'Optional'} — ${f.description}`).join('\n'),
      ``,
      `Response Codes:`,
      docs.responseCodes?.map((r) => `  ${r.code}: ${r.meaning}`).join('\n'),
      ``,
      `Sample curl:`,
      docs.curlExample,
    ].join('\n');
    copy(text, 'all');
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel p-0 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-civix-border/40">
          <div>
            <h2 className="font-display text-xl font-bold">Integration Documentation</h2>
            <div className="text-xs text-white/50 mt-0.5">Share these with your lead vendor</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyAllDocs}
              className="btn-ghost text-sm flex items-center gap-1.5"
            >
              {copied === 'all' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              Copy All Docs
            </button>
            <button
              onClick={onSendEmail}
              className="btn-primary text-sm flex items-center gap-1.5"
            >
              <Send className="w-4 h-4" />
              Send to Vendor Email
            </button>
            <button onClick={onClose} className="btn-ghost text-sm px-2">✕</button>
          </div>
        </div>

        {/* Modal body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {isLoading && <div className="text-white/50 text-sm">Loading integration docs…</div>}
          {docs && (
            <>
              {/* Endpoint */}
              <DocSection title="Endpoint">
                <div className="flex items-center gap-2 bg-civix-bg/60 rounded-lg px-4 py-3 border border-civix-border/40">
                  <span className="text-xs font-bold text-emerald-400 mr-2">POST</span>
                  <code className="text-sm text-civix-cyan flex-1 break-all">{docs.endpoint}</code>
                  <button onClick={() => copy(docs.endpoint, 'endpoint')} className="text-white/50 hover:text-white shrink-0">
                    {copied === 'endpoint' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </DocSection>

              {/* Headers */}
              <DocSection title="Required Headers">
                <div className="bg-civix-bg/60 rounded-lg px-4 py-3 border border-civix-border/40 font-mono text-sm text-white/80">
                  Content-Type: application/json
                </div>
              </DocSection>

              {/* API Key */}
              <DocSection title="API Key">
                <div className="flex items-center gap-2 bg-civix-bg/60 rounded-lg px-4 py-3 border border-civix-border/40">
                  <code className="font-mono text-sm text-amber-300 flex-1 break-all">
                    {showKey ? docs.apiKey : docs.apiKey.slice(0, 8) + '●'.repeat(48) + docs.apiKey.slice(-8)}
                  </code>
                  <button onClick={() => setShowKey(!showKey)} className="text-white/50 hover:text-white shrink-0">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => copy(docs.apiKey, 'key')} className="text-white/50 hover:text-white shrink-0">
                    {copied === 'key' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-xs text-amber-400/70 mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Keep this key secret — it authorizes lead submissions to your account.
                </div>
              </DocSection>

              {/* Sample Payload */}
              <DocSection title="Sample JSON Payload">
                <div className="relative">
                  <pre className="bg-civix-bg/80 rounded-lg p-4 text-xs text-civix-cyan overflow-x-auto border border-civix-border/40">
                    {JSON.stringify(docs.samplePayload, null, 2)}
                  </pre>
                  <button
                    onClick={() => copy(JSON.stringify(docs.samplePayload, null, 2), 'payload')}
                    className="absolute top-3 right-3 text-white/40 hover:text-white"
                  >
                    {copied === 'payload' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </DocSection>

              {/* Field reference table */}
              <DocSection title="Field Reference">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase text-white/40 border-b border-civix-border/40">
                        <th className="py-2 pr-4 text-left">Field</th>
                        <th className="py-2 pr-4 text-left">Type</th>
                        <th className="py-2 pr-4 text-left">Required</th>
                        <th className="py-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.fieldReference?.map((f) => (
                        <tr key={f.name} className="border-b border-civix-border/20">
                          <td className="py-2 pr-4 font-mono text-xs text-civix-cyan">{f.name}</td>
                          <td className="py-2 pr-4 text-white/60 text-xs">{f.type}</td>
                          <td className="py-2 pr-4">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${f.required ? 'bg-red-500/20 text-red-300' : 'bg-sky-500/20 text-sky-300'}`}>
                              {f.required ? 'Required' : 'Optional'}
                            </span>
                          </td>
                          <td className="py-2 text-white/55 text-xs">{f.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DocSection>

              {/* Sample curl */}
              <DocSection title="Sample curl">
                <div className="relative">
                  <pre className="bg-civix-bg/80 rounded-lg p-4 text-xs text-white/80 overflow-x-auto border border-civix-border/40 whitespace-pre-wrap">
                    {docs.curlExample}
                  </pre>
                  <button
                    onClick={() => copy(docs.curlExample, 'curl')}
                    className="absolute top-3 right-3 text-white/40 hover:text-white"
                  >
                    {copied === 'curl' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </DocSection>

              {/* Response codes */}
              <DocSection title="Response Codes">
                <div className="space-y-1.5">
                  {docs.responseCodes?.map((r) => (
                    <div key={r.code} className="flex items-start gap-3 text-sm">
                      <span className={`font-mono font-bold text-xs px-2 py-0.5 rounded shrink-0 ${
                        r.code === 200 ? 'bg-emerald-500/20 text-emerald-400' :
                        r.code === 401 ? 'bg-red-500/20 text-red-400' :
                        r.code === 422 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>{r.code}</span>
                      <span className="text-white/65">{r.meaning}</span>
                    </div>
                  ))}
                </div>
              </DocSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function DocSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{title}</div>
      {children}
    </div>
  );
}
